import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import { NumberParam, useQueryParam } from 'next-query-params';
import { useRouter } from 'next/router';
import { bool, func, node, oneOf, oneOfType, shape, string } from 'prop-types';
import React from 'react';
import getFlow, { MLC_AVAILABILITY_FLOW_INDEX } from '../../../modules/Flow';
import Notes from '../../admin/notes';
import ProgressManager from '../../admin/progressManager';
import TrustBanner from '../../shared/TrustBanners/TrustBanner';
import Form from '../../ui-kit/Form';
import Alert from './Alert';
import Header from './Header';
import Heading from './Heading';
import Metadata from './Metadata';
import NavigationBar from './NavigationBar';

const useStyles = makeStyles(theme => ({
  trustBannerMobile: {
    display: 'flex',
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  trustBannerDesktop: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
    },
  },
}));

const Layout = ({
  children,
  heading,
  subheading,
  helper,
  icon,
  form: { onValid, onInvalid, isForm },
  button,
  alert,
  reservation,
  overrideParams,
  noContinue,
}) => {
  const classes = useStyles();
  const { pathname } = useRouter();
  const flow = getFlow();
  const [flowNumber] = useQueryParam('flow', NumberParam);
  const currentPageIndex = flow.findIndex(path => path?.includes(pathname));

  const isLastPageInFlow = currentPageIndex === flow?.length - 1;

  const handleOnValid = () => {
    onValid();
  };

  const handleOnInvalid = () => {
    onInvalid();
  };

  return (
    <Grid
      container
      component={Box}
      bgcolor="background.light"
      minHeight={{ xs: 'calc(100vh - 51px)', md: 'calc(100vh - 105px)' }}
    >
      <Metadata />
      <Header currentPageIndex={currentPageIndex} flow={flow} />
      <Box pt={{ xs: 30, sm: 8, md: 11 }}>
        <Box className={classes.trustBannerDesktop}>
          {process.env.NEXT_PUBLIC_ASSEMBLY !== 'true' ? (
            <TrustBanner />
          ) : (
            <ProgressManager currentPageIndex={currentPageIndex} flow={flow} />
          )}
        </Box>
      </Box>
      <Box
        width="100%"
        px={{ xs: 2, sm: 2, md: 0 }}
        pt={{ xs: 10, sm: 5 }}
        pb={{ xs: 20, sm: 10, md: 20 }}
        height="100%"
        bgcolor="background.light"
        display="flex"
        flexDirection="column"
      >
        <Box>
          {(heading || subheading) && <Heading heading={heading} subheading={subheading} icon={icon} helper={helper} />}
          <Box
            display={{ xs: 'flex', md: 'none' }}
            alignItems="center"
            justifyContent="center"
            width={350}
            margin="0 auto"
            mt={-3}
            mb={2}
          >
            {alert?.message && <Alert alert={alert} />}
          </Box>
        </Box>
        <Box order={{ xs: 2, sm: 3 }}>
          {isForm ? (
            <Form onValid={handleOnValid} onInvalid={handleOnInvalid}>
              {children}
              <Box className={classes.trustBannerMobile}>
                <TrustBanner />
              </Box>
              {!isLastPageInFlow && (
                <NavigationBar
                  {...button}
                  currentPageIndex={currentPageIndex}
                  flow={flow}
                  alert={alert}
                  reservation={reservation}
                  overrideParams={overrideParams}
                />
              )}
            </Form>
          ) : (
            <>
              {children}
              <Box className={classes.trustBannerMobile}>
                <TrustBanner />
              </Box>
              {(!isLastPageInFlow || flowNumber === MLC_AVAILABILITY_FLOW_INDEX) && (
                <NavigationBar
                  noContinue={noContinue}
                  {...button}
                  currentPageIndex={currentPageIndex}
                  flow={flow}
                  alert={alert}
                  reservation={reservation}
                />
              )}
            </>
          )}
        </Box>
        {process.env.NEXT_PUBLIC_ASSEMBLY === 'true' && <Notes />}
      </Box>
    </Grid>
  );
};

Layout.propTypes = {
  children: node.isRequired,
  heading: oneOfType([node, string]),
  icon: shape({
    svg: string,
    alt: string,
  }),
  subheading: string,
  helper: string,
  button: shape({
    text: string,
    onClick: func,
    isDisabled: bool,
    isLoading: bool,
    isReady: bool,
  }),
  form: shape({
    isForm: bool,
    onValid: func,
    onInvalid: func,
  }),
  alert: shape({
    type: oneOf(['error', 'secondary']),
    message: string,
  }),
  reservation: shape({}),
  overrideParams: string,
  noContinue: bool,
};

Layout.defaultProps = {
  icon: {
    svg: '',
    alt: '',
  },
  heading: '',
  subheading: '',
  helper: '',
  button: {
    text: 'Continue',
    isReady: true,
    onClick: () => {},
    isDisabled: false,
    isLoading: false,
    ready: true,
  },
  form: {
    isForm: false,
    onValid: () => {},
    onInvalid: () => {},
  },
  alert: {},
  reservation: null,
  overrideParams: null,
  noContinue: false,
};

export default Layout;
